import { ReactElement } from 'react';
import {
  Icons,
  StatusChipV2,
  type StatusChipV2Color,
  type StatusChipV2Props,
} from '@cast/design-system';
import { ClusterDisplayStatus } from '@cast/types';

const clusterLabelMap: Record<ClusterDisplayStatus, string> = {
  [ClusterDisplayStatus.WAITING_CONNECTION]: 'Connecting',
  [ClusterDisplayStatus.CONNECTING]: 'Connecting',
  [ClusterDisplayStatus.READ_ONLY]: 'Read only',
  [ClusterDisplayStatus.READY]: 'Connected',
  [ClusterDisplayStatus.HIBERNATED]: 'Hibernated',
  [ClusterDisplayStatus.HIBERNATING]: 'Hibernating',
  [ClusterDisplayStatus.RESUMING]: 'Resuming',
  [ClusterDisplayStatus.DELETED]: 'Deleted',
  [ClusterDisplayStatus.DELETING]: 'Deleting',
  [ClusterDisplayStatus.WARNING]: 'Warning',
  [ClusterDisplayStatus.FAILED]: 'Failed',
  [ClusterDisplayStatus.NOT_FOUND]: 'Not found',
  [ClusterDisplayStatus.ONLINE]: 'Online',
  [ClusterDisplayStatus.DISCONNECTED]: 'Disconnected',
  [ClusterDisplayStatus.DISCONNECTING]: 'Disconnecting',
  [ClusterDisplayStatus.NOT_RESPONDING]: 'Not Responding',
  [ClusterDisplayStatus.DISCOVERED]: 'Discovered',
  [ClusterDisplayStatus.ARCHIVED]: 'Archived',
};

const clusterIconMap: Partial<Record<ClusterDisplayStatus, ReactElement>> = {
  [ClusterDisplayStatus.READ_ONLY]: <Icons.Eye />,
  [ClusterDisplayStatus.READY]: <Icons.CheckCircle />,
  [ClusterDisplayStatus.HIBERNATED]: <Icons.Pause />,
  [ClusterDisplayStatus.WARNING]: <Icons.Warning />,
  [ClusterDisplayStatus.FAILED]: <Icons.Warning />,
  [ClusterDisplayStatus.NOT_FOUND]: <Icons.WarningCircle />,
  [ClusterDisplayStatus.ONLINE]: <Icons.CheckCircle />,
  [ClusterDisplayStatus.DISCONNECTED]: <Icons.XCircle />,
  [ClusterDisplayStatus.NOT_RESPONDING]: <Icons.Warning />,
};

const inProgressStatus: ClusterDisplayStatus[] = [
  ClusterDisplayStatus.WAITING_CONNECTION,
  ClusterDisplayStatus.CONNECTING,
  ClusterDisplayStatus.HIBERNATING,
  ClusterDisplayStatus.RESUMING,
  ClusterDisplayStatus.DELETING,
  ClusterDisplayStatus.DISCONNECTING,
];

const getStatusColor = (status: ClusterDisplayStatus): StatusChipV2Color => {
  if (
    [
      ClusterDisplayStatus.ONLINE,
      ClusterDisplayStatus.READ_ONLY,
      ClusterDisplayStatus.READY,
    ].includes(status)
  ) {
    return 'success';
  }

  if (
    [
      ClusterDisplayStatus.NOT_RESPONDING,
      ClusterDisplayStatus.WARNING,
    ].includes(status)
  ) {
    return 'warning';
  }

  if (
    [
      ClusterDisplayStatus.DISCONNECTED,
      ClusterDisplayStatus.FAILED,
      ClusterDisplayStatus.NOT_FOUND,
    ].includes(status)
  ) {
    return 'error';
  }

  if (status === ClusterDisplayStatus.RESUMING) {
    return 'info';
  }

  return 'neutral';
};

type ClusterStatusChipProps = Omit<
  StatusChipV2Props,
  'label' | 'color' | 'loading' | 'slots'
> & {
  status: ClusterDisplayStatus;
  managed?: boolean;
  noIcon?: boolean;
  noCaption?: boolean;
};

export const ClusterStatusChip = ({
  status,
  managed,
  size = 'medium',
  noIcon,
  noCaption,
  ...chipProps
}: ClusterStatusChipProps) => {
  let caption =
    !noCaption && !managed && status !== ClusterDisplayStatus.READ_ONLY
      ? clusterLabelMap[ClusterDisplayStatus.READ_ONLY]
      : undefined;

  if (
    status === ClusterDisplayStatus.CONNECTING ||
    status === ClusterDisplayStatus.WAITING_CONNECTION
  ) {
    caption = `Enabling Cast AI...`;
  }

  const isInProgress = inProgressStatus.includes(status);
  let icon: ReactElement | undefined;

  if (!noIcon && !isInProgress) {
    if (!managed) {
      icon = <Icons.Eye />;
    } else {
      icon = clusterIconMap[status];
    }
  }

  return (
    <StatusChipV2
      label={clusterLabelMap[status] || 'Unknown'}
      color={getStatusColor(status)}
      variant="subtle"
      size={size}
      caption={caption}
      loading={isInProgress}
      slots={icon ? { icon } : undefined}
      testId="cluster-status-chip"
      {...chipProps}
    />
  );
};
